import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  commitStaffImportJob,
  createStaffImportJob,
  downloadStaffImportTemplate,
  previewStaffImportJob,
  rollbackStaffImportJob,
} from "../../api/staffImport";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import {
  MODAL_TEXT,
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_PRIMARY,
  BTN_OUTLINE,
  BTN_DANGER,
} from "../../ui/modalStyles";
import SBINModal from "../modals/SBINModal";
import InfoTooltip from "../common/InfoTooltip";
import ReportHelpCard from "../reports/ReportHelpCard";

const STEP_LABELS = [
  "1. Upload",
  "2. Column Mapping",
  "3. Preview",
  "4. Commit",
];

const FIELD_DEFS = [
  { key: "display_name", label: "Display Name (required)" },
  { key: "role", label: "Role" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "is_active", label: "Active (Yes/No)" },
  { key: "is_booth_renter", label: "Booth Renter (Yes/No)" },
  { key: "show_in_online_booking", label: "Show in Online Booking (Yes/No)" },
  { key: "display_order", label: "Display Order" },
  { key: "academy_id", label: "Academy ID" },
  { key: "academy_level", label: "Academy Level" },
  { key: "notes", label: "Notes" },
  { key: "skills", label: "Skills (comma-separated)" },
  { key: "skills_other", label: "Skills (Other)" },
  { key: "languages", label: "Languages (comma-separated)" },
  { key: "languages_other", label: "Languages (Other)" },
  { key: "years_experience", label: "Years Experience" },
  { key: "years_experience_other", label: "Years Experience (Other)" },
  { key: "is_certified", label: "Certified (Yes/No)" },
];

const SYNONYMS = {
  display_name: ["name", "display name", "staff name", "employee name"],
  role: ["role", "position", "title", "job title"],
  phone: ["phone", "mobile", "cell", "telephone", "phone number"],
  email: ["email", "e-mail", "email address"],
  is_active: ["active", "is active", "active status", "status"],
  is_booth_renter: ["booth renter", "is booth renter", "booth rental", "renter"],
  show_in_online_booking: [
    "show in online booking",
    "online booking",
    "show online",
    "online visibility",
  ],
  display_order: ["display order", "order", "schedule order", "sort order"],
  academy_id: ["academy id", "academy", "academy identifier"],
  academy_level: ["academy level", "level"],
  notes: ["note", "notes", "comment", "comments"],
  skills: ["skills", "skill", "skill set"],
  skills_other: ["skills other", "other skills"],
  languages: ["languages", "language"],
  languages_other: ["languages other", "other languages"],
  years_experience: ["years experience", "experience", "years of experience"],
  years_experience_other: ["years experience other", "other experience"],
  is_certified: ["certified", "is certified", "certification"],
};

const ISSUE_LABELS = {
  DISPLAY_NAME_REQUIRED: "Display name required",
  EMAIL_INVALID: "Email invalid",
  BOOLEAN_INVALID: "Invalid Yes/No",
  DISPLAY_ORDER_INVALID: "Display order invalid",
  DUPLICATE_IN_FILE: "Duplicate in file",
  CONFLICTING_MATCHES: "Conflicting matches",
  MULTIPLE_MATCHES: "Multiple matches",
  MATCH_REQUIRED: "Match required",
  UPDATE_NOT_ALLOWED: "Update not allowed",
  REQUIRED_FIELDS: "Missing required fields",
  PHONE_FORMAT_UNUSUAL: "Phone format unusual",
  ROLE_DEFAULTED: "Role defaulted",
};

const HELP_SECTIONS = [
  {
    id: "upload",
    title: "Step 1: Upload file",
    description: "Download the template, fill your staff rows, then upload the file.",
    anchorId: "si-step-1",
    bullets: [
      "Use CSV or XLSX format.",
      "Confirm the salon before continuing.",
      "Choose whether existing staff updates are allowed.",
    ],
    tips: [
      "Use the template headers exactly for best auto-mapping results.",
      "Start with a small test file before a full import.",
    ],
  },
  {
    id: "mapping",
    title: "Step 2: Column mapping",
    description: "Map file columns to staff fields before preview.",
    anchorId: "si-step-2",
    bullets: [
      "Display Name must be mapped.",
      "Unmapped optional fields are allowed.",
      "Review role, email, and phone mappings carefully.",
    ],
    tips: [
      "If mapping looks wrong, go back and re-check template headers.",
      "Keep one source column per field to avoid confusion.",
    ],
  },
  {
    id: "preview",
    title: "Step 3: Preview and fix",
    description: "Review row actions, match updates, and resolve issues.",
    anchorId: "si-step-3",
    bullets: [
      "Check creates, updates, skips, and errors.",
      "For UPDATE rows, choose the correct staff match.",
      "Set problematic rows to SKIP if needed.",
    ],
    tips: [
      "Resolve all errors before continuing.",
      "Use pagination to inspect a sample from each page.",
    ],
  },
  {
    id: "commit",
    title: "Step 4: Commit and rollback",
    description: "Commit the import, review result counts, and rollback if required.",
    anchorId: "si-step-4",
    bullets: [
      "Commit Import applies creates and updates.",
      "Use Undo Import only when immediate rollback is required.",
      "Download error CSV if some rows fail.",
    ],
    tips: [
      "Review import history after commit.",
      "Only rollback right away before more changes happen.",
    ],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Upload fails",
    body: "Check file format, salon context, and required headers.",
  },
  {
    title: "Rows stuck in review",
    body: "Set row to SKIP or pick a valid match for UPDATE.",
  },
  {
    title: "Commit blocked",
    body: "Resolve preview errors first, then continue to commit.",
  },
];

const TOOLTIPS = {
  upload_file: "Upload CSV or XLSX with staff rows.",
  salon: "Import will only affect staff in this salon.",
  allow_updates: "Enable updates for matched existing staff records.",
  mapping: "Map each file column to the correct staff field.",
  action: "Choose create, update, skip, or review action per row.",
  match: "Required when action is UPDATE.",
  issues: "Errors must be resolved or skipped before commit.",
  commit: "Final step that writes changes to staff records.",
};

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function autoMapHeaders(headers = []) {
  const normalized = headers.map((header) => ({
    raw: header,
    norm: normalizeHeader(header),
  }));
  const used = new Set();
  const mapping = {};

  FIELD_DEFS.forEach((field) => {
    const synonyms = SYNONYMS[field.key] || [];
    const match = normalized.find(
      (header) =>
        !used.has(header.raw) &&
        (synonyms.includes(header.norm) || header.norm === normalizeHeader(field.key))
    );
    if (match) {
      mapping[field.key] = match.raw;
      used.add(match.raw);
    } else {
      mapping[field.key] = "";
    }
  });
  return mapping;
}

function buildPageRange(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const output = [];
  let last = 0;
  for (const page of sorted) {
    if (page - last > 1) {
      output.push("ellipsis");
    }
    output.push(page);
    last = page;
  }
  return output;
}

function paginateRows(rows, page, perPage) {
  const total = rows.length;
  const startIndex = (page - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, total);
  return {
    rows: rows.slice(startIndex, endIndex),
    total,
    startIndex: total === 0 ? 0 : startIndex + 1,
    endIndex,
  };
}

function issueCode(issue) {
  if (!issue) return "UNKNOWN";
  return typeof issue === "string" ? issue : issue.code || "UNKNOWN";
}

export default function StaffImportWizard({
  embedded = false,
  initialSalonId = null,
  onComplete,
  onClose,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedSalonId, setSelectedSalonId] = useState(null);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [jobId, setJobId] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [mapping, setMapping] = useState({});
  const [allowUpdates, setAllowUpdates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [rowOverrides, setRowOverrides] = useState({});
  const [commitResult, setCommitResult] = useState(null);
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackReport, setRollbackReport] = useState(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  const currentSalonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const hideSalonSelector = true;
  const resolvedSalonId = initialSalonId ?? currentSalonId;
  const isSalonLocked = hideSalonSelector || Boolean(initialSalonId);

  useEffect(() => {
    if (!resolvedSalonId) return;
    setSelectedSalonId(resolvedSalonId);
    setError((prev) => {
      if (
        prev === "Select a salon before importing staff." ||
        prev === "No salon context found. Please log in again."
      ) {
        return "";
      }
      return prev;
    });
  }, [resolvedSalonId]);

  useEffect(() => {
    if (selectedSalonId || resolvedSalonId) return;
    if (isSalonLocked) {
      setError("No salon context found. Please log in again.");
    } else {
      setError("Select a salon before importing staff.");
    }
    const timeout = setTimeout(() => {
      navigate("/my-salons", { replace: true });
    }, 600);
    return () => clearTimeout(timeout);
  }, [selectedSalonId, resolvedSalonId, isSalonLocked, navigate]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const optionsPayload = useMemo(
    () => ({
      allow_updates: allowUpdates,
    }),
    [allowUpdates]
  );

  const previewRows = useMemo(() => preview?.rows || [], [preview]);

  const pageInfo = useMemo(
    () => paginateRows(previewRows, previewPage, 25),
    [previewRows, previewPage]
  );

  const mappingErrors = useMemo(() => {
    const errors = [];
    const hasDisplayName = mapping.display_name && mapping.display_name.trim();
    if (!hasDisplayName) errors.push("Map Display Name");
    return errors;
  }, [mapping]);

  const mappingIsValid = mappingErrors.length === 0;

  const unresolvedPreviewErrors = useMemo(() => {
    return previewRows.some((row) => {
      const errors = row.errors || [];
      const override = rowOverrides[row.row_id];
      const action =
        (override?.action || row.proposed_action || "CREATE").toUpperCase();
      const matchId = override?.match_staff_id || row.match_staff_id;
      if (action === "UPDATE" && !matchId) return true;
      if (errors.length === 0) return false;
      if (action === "SKIP") return false;
      return true;
    });
  }, [previewRows, rowOverrides]);

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const stepByAnchor = {
        "si-step-1": 1,
        "si-step-2": 2,
        "si-step-3": 3,
        "si-step-4": 4,
      };
      const targetStep = stepByAnchor[id];
      const runHighlight = () => {
        const jumped = scrollToSection(id);
        if (!jumped) return;
        setHighlightedSectionId(id);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedSectionId((current) => (current === id ? "" : current));
        }, 1500);
      };

      if (targetStep && targetStep !== step) {
        setStep(targetStep);
        setTimeout(runHighlight, 120);
        return;
      }
      runHighlight();
    },
    [scrollToSection, step]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const handleDownloadTemplate = async () => {
    setError("");
    try {
      const blob = await downloadStaffImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "staff_import_template.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Unable to download template.");
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedSalonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await createStaffImportJob({
        file,
        salon_id: selectedSalonId,
      });
      setJobId(data.job_id);
      const nextHeaders = Array.isArray(data.headers) ? data.headers : [];
      setHeaders(nextHeaders);
      setRowCount(Number(data.row_count || 0));
      const autoMapping = autoMapHeaders(nextHeaders);
      setMapping(autoMapping);
      setPreview(null);
      setRowOverrides({});
      setCommitResult(null);
      setRollbackReport(null);
      setPreviewPage(1);
      setStep(2);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to upload file."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!mappingIsValid || !jobId) return;
    setPreviewLoading(true);
    setError("");
    try {
      const data = await previewStaffImportJob(jobId, {
        mapping,
        options: optionsPayload,
      });
      setPreview(data);
      setRowOverrides({});
      setPreviewPage(1);
      setStep(3);
      setRollbackReport(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to preview import."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!jobId) return;
    setCommitLoading(true);
    setError("");
    try {
      const overrides = Object.entries(rowOverrides).map(([rowId, data]) => ({
        row_id: rowId,
        action: data.action,
        match_staff_id: data.match_staff_id || null,
      }));
      const data = await commitStaffImportJob(jobId, {
        options: optionsPayload,
        overrides,
      });
      setCommitResult(data);
      setRollbackReport(null);
      if (typeof onComplete === "function") {
        onComplete(data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to commit import."
      );
    } finally {
      setCommitLoading(false);
    }
  };

  const handleOverrideAction = (rowId, action) => {
    setRowOverrides((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        action,
      },
    }));
  };

  const handleOverrideMatch = (rowId, matchStaffId) => {
    setRowOverrides((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        match_staff_id: matchStaffId ? Number(matchStaffId) : null,
      },
    }));
  };

  const handleRollback = async () => {
    if (!commitResult?.job_id) return;
    setRollbackLoading(true);
    setError("");
    try {
      const data = await rollbackStaffImportJob(commitResult.job_id);
      setRollbackReport(data);
      setRollbackModalOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to roll back this import."
      );
    } finally {
      setRollbackLoading(false);
    }
  };

  const renderPagination = (page, totalRows, setPage) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / 25));
    const pages = buildPageRange(page, totalPages);
    return (
      <div className="flex items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {"<"}
        </button>
        {pages.map((entry, idx) =>
          entry === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">
              ...
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => setPage(entry)}
              className={`h-10 w-10 rounded-full border text-sm md:text-base ${
                entry === page
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {entry}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {">"}
        </button>
      </div>
    );
  };

  const stepHeader = (
    <div className="flex flex-wrap gap-2">
      {STEP_LABELS.map((label, idx) => {
        const isActive = step === idx + 1;
        return (
          <span
            key={label}
            className={`h-9 px-4 rounded-full text-sm md:text-base font-semibold flex items-center ${
              isActive
                ? "bg-slate-900 text-white"
                : "border border-slate-200 text-slate-600"
            }`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );

  const renderStepOne = (
    <div id="si-step-1" className={getSectionClassName("si-step-1", "space-y-5")}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className={BTN_OUTLINE}
        >
          Download template
        </button>
        <div className="text-sm md:text-base text-slate-500">
          Upload CSV or XLSX with your staff data.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
            <span>Upload File</span>
            <InfoTooltip text={TOOLTIPS.upload_file} label="Upload File" />
          </div>
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            className={`mt-1 w-full ${FIELD_CLASS}`}
          />
        </div>
        <div>
          <div className={`${LABEL_TEXT} inline-flex items-center gap-2`}>
            <span>Salon</span>
            <InfoTooltip text={TOOLTIPS.salon} label="Salon" />
          </div>
          <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm md:text-base text-slate-700">
            Salon: {salonName || "Unknown salon"}
            {selectedSalonId ? ` (ID ${selectedSalonId})` : ""}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={allowUpdates}
            onChange={(event) => setAllowUpdates(event.target.checked)}
          />
          <span>Allow updating existing staff</span>
          <InfoTooltip text={TOOLTIPS.allow_updates} label="Allow Updates" />
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleUpload}
          className={BTN_PRIMARY}
          disabled={loading || !file || !selectedSalonId}
        >
          {loading ? "Uploading..." : "Continue"}
        </button>
      </div>
    </div>
  );

  const renderStepTwo = (
    <div id="si-step-2" className={getSectionClassName("si-step-2", "space-y-5")}>
      <div className="inline-flex items-center gap-2 text-sm md:text-base text-slate-600">
        Detected {headers.length} columns from the file. Map them to staff fields.
        <InfoTooltip text={TOOLTIPS.mapping} label="Column Mapping" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELD_DEFS.map((field) => (
          <div key={field.key}>
            <div className={LABEL_TEXT}>{field.label}</div>
            <select
              value={mapping[field.key] || ""}
              onChange={(event) =>
                setMapping((prev) => ({
                  ...prev,
                  [field.key]: event.target.value,
                }))
              }
              className={`mt-1 w-full ${FIELD_CLASS}`}
            >
              <option value="">Not mapped</option>
              {headers.map((header) => (
                <option key={`${field.key}-${header}`} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {mappingErrors.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          {`Please resolve: ${mappingErrors.join(", ")}.`}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(1)}
          className={BTN_OUTLINE}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handlePreview}
          className={BTN_PRIMARY}
          disabled={!mappingIsValid || previewLoading}
        >
          {previewLoading ? "Validating..." : "Continue"}
        </button>
      </div>
    </div>
  );

  const renderStepThree = (
    <div id="si-step-3" className={getSectionClassName("si-step-3", "space-y-6")}>
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        {[
          ["Total rows", preview?.summary?.total_rows],
          ["Creates", preview?.summary?.creates],
          ["Updates", preview?.summary?.updates],
          ["Skips", preview?.summary?.skips],
          ["Errors", preview?.summary?.errors],
          ["Duplicate in file", preview?.summary?.duplicate_in_file],
          ["Conflicts", preview?.summary?.conflicts],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200 bg-white p-4"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {value ?? 0}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {renderPagination(previewPage, pageInfo.total, setPreviewPage)}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Row #</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Action
                    <InfoTooltip text={TOOLTIPS.action} label="Action" />
                  </span>
                </th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Match
                    <InfoTooltip text={TOOLTIPS.match} label="Match" />
                  </span>
                </th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Issues
                    <InfoTooltip text={TOOLTIPS.issues} label="Issues" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm md:text-base text-slate-800">
              {pageInfo.rows.map((row) => {
                const override = rowOverrides[row.row_id];
                const action =
                  (override?.action || row.proposed_action || "CREATE").toUpperCase();
                const hasReview = action === "REVIEW";
                const options = allowUpdates
                  ? ["CREATE", "UPDATE", "SKIP"]
                  : ["CREATE", "SKIP"];
                const matchCandidates = row.match_candidates || [];
                const matchId = override?.match_staff_id || row.match_staff_id || "";
                return (
                  <tr
                    key={row.row_id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-3 py-2">{row.row_number}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">
                      {row.display_name || "-"}
                    </td>
                    <td className="px-3 py-2">{row.role || "-"}</td>
                    <td className="px-3 py-2">{row.phone || "-"}</td>
                    <td className="px-3 py-2">{row.email || "-"}</td>
                    <td className="px-3 py-2">
                      <select
                        value={hasReview ? "REVIEW" : action}
                        onChange={(event) =>
                          handleOverrideAction(row.row_id, event.target.value)
                        }
                        className={FIELD_CLASS}
                      >
                        {hasReview && (
                          <option value="REVIEW" disabled>
                            Review
                          </option>
                        )}
                        {options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {action === "UPDATE" ? (
                        matchCandidates.length > 0 ? (
                          <select
                            value={matchId || ""}
                            onChange={(event) =>
                              handleOverrideMatch(row.row_id, event.target.value)
                            }
                            className={FIELD_CLASS}
                          >
                            <option value="">Select staff</option>
                            {matchCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.display_name}{" "}
                                {candidate.role ? `(${candidate.role})` : ""}
                                {candidate.phone_masked
                                  ? `(${candidate.phone_masked})`
                                  : candidate.email_masked
                                  ? `(${candidate.email_masked})`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-slate-500">No match</span>
                        )
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {(row.errors || []).map((issue, idx) => {
                          const code = issueCode(issue);
                          return (
                            <span
                              key={`${row.row_id}-err-${idx}`}
                              className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"
                            >
                              {ISSUE_LABELS[code] || code}
                            </span>
                          );
                        })}
                        {(row.warnings || []).map((issue, idx) => {
                          const code = issueCode(issue);
                          return (
                            <span
                              key={`${row.row_id}-warn-${idx}`}
                              className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"
                            >
                              {ISSUE_LABELS[code] || code}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pageInfo.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-6 text-center text-sm md:text-base text-slate-500"
                  >
                    No preview rows available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-xs md:text-sm text-slate-500">
          Showing {pageInfo.startIndex} to {pageInfo.endIndex} of {pageInfo.total}{" "}
          entries
        </div>
      </div>

      {unresolvedPreviewErrors && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          Resolve rows with errors before continuing (set them to Skip or select a
          match).
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(2)}
          className={BTN_OUTLINE}
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => setStep(4)}
          className={BTN_PRIMARY}
          disabled={unresolvedPreviewErrors}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderStepFour = (
    <div id="si-step-4" className={getSectionClassName("si-step-4", "space-y-5")}>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ready to import
        </div>
        <div className="mt-1 text-sm md:text-base text-slate-700">
          {preview?.summary?.creates ?? 0} creates, {preview?.summary?.updates ?? 0}{" "}
          updates, {preview?.summary?.skips ?? 0} skips.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(3)}
          className={BTN_OUTLINE}
          disabled={commitLoading}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleCommit}
          className={BTN_PRIMARY}
          disabled={commitLoading}
        >
          <span className="inline-flex items-center gap-2">
            {commitLoading ? "Importing..." : "Commit Import"}
            <InfoTooltip text={TOOLTIPS.commit} label="Commit Import" />
          </span>
        </button>
      </div>

      {commitResult && (
        <div className="space-y-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm md:text-base text-emerald-700">
            Import complete. {commitResult.created_count} created,{" "}
            {commitResult.updated_count} updated, {commitResult.skipped_count} skipped,{" "}
            {commitResult.failed_count} failed.
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate("/manager/manage/staff-imports")}
              className={BTN_OUTLINE}
            >
              View Import History
            </button>
            <button
              type="button"
              onClick={() => setRollbackModalOpen(true)}
              className={BTN_DANGER}
              disabled={rollbackLoading || Boolean(rollbackReport)}
            >
              {rollbackLoading ? "Rolling back..." : "Undo Import"}
            </button>
          </div>

          {commitResult.failed_count > 0 && (
            <button
              type="button"
              onClick={() => {
                const rows = commitResult.failures || [];
                const header = "row_number,errors\n";
                const lines = rows.map((row) => {
                  const errors = (row.errors || []).join("; ");
                  return `${row.row_number},"${errors.replaceAll('"', '""')}"\n`;
                });
                const blob = new Blob([header, ...lines], {
                  type: "text/csv;charset=utf-8;",
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "staff_import_errors.csv";
                link.click();
                window.URL.revokeObjectURL(url);
              }}
              className={BTN_OUTLINE}
            >
              Download Error Report CSV
            </button>
          )}

          {rollbackReport && (
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm md:text-base text-slate-700">
                Rollback complete. {rollbackReport.deleted_created_staff_count} created
                staff deleted, {rollbackReport.reverted_updated_staff_count} updates reverted,{" "}
                {rollbackReport.skipped} skipped.
              </div>
              {(rollbackReport.skipped_details || []).length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="min-w-full">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Staff ID</th>
                        <th className="px-3 py-2">Action</th>
                        <th className="px-3 py-2">Reasons</th>
                        <th className="px-3 py-2">Changed Fields</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm md:text-base text-slate-700">
                      {rollbackReport.skipped_details.map((entry, idx) => (
                        <tr
                          key={`${entry.staff_id}-${idx}`}
                          className="border-t border-slate-100 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2">{entry.staff_id}</td>
                          <td className="px-3 py-2 uppercase text-xs font-semibold text-slate-500">
                            {entry.action}
                          </td>
                          <td className="px-3 py-2">
                            {(entry.reasons || []).join(", ")}
                          </td>
                          <td className="px-3 py-2">
                            {(entry.changed_fields || []).join(", ") || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {typeof onClose === "function" && (
            <button
              type="button"
              onClick={onClose}
              className={BTN_OUTLINE}
            >
              Done
            </button>
          )}
        </div>
      )}
    </div>
  );

  const wizardBody = (
    <div className={`space-y-6 ${MODAL_TEXT}`}>
      <div id="si-overview">
        <ReportHelpCard
          title="Help Guide: Import Staff"
          subtitle="Follow these steps to upload, validate, and commit staff imports safely."
          sections={HELP_SECTIONS}
          troubleshooting={TROUBLESHOOTING_ITEMS}
          onJumpToSection={jumpToSectionWithHighlight}
        />
      </div>
      {stepHeader}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          {typeof error === "string" ? error : "Something went wrong."}
        </div>
      )}
      {step === 1 && renderStepOne}
      {step === 2 && renderStepTwo}
      {step === 3 && renderStepThree}
      {step === 4 && renderStepFour}
    </div>
  );

  const rollbackModal = (
    <SBINModal
      isOpen={rollbackModalOpen}
      onClose={() => setRollbackModalOpen(false)}
      title="Undo Import"
      size="lg"
      fitContent
    >
      <div className="space-y-4">
        <p className="text-sm md:text-base text-slate-700">
          This will roll back the import. Created staff are deleted only if they
          have no appointments, schedules, time off, extra hours, or services. Updated
          staff revert only if they have not been modified since the import.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setRollbackModalOpen(false)}
            className={BTN_OUTLINE}
            disabled={rollbackLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRollback}
            className={BTN_DANGER}
            disabled={rollbackLoading}
          >
            {rollbackLoading ? "Rolling back..." : "Confirm Rollback"}
          </button>
        </div>
      </div>
    </SBINModal>
  );

  if (!embedded) {
    return (
      <>
        {wizardBody}
        {rollbackModal}
      </>
    );
  }

  return (
    <>
      <div
        className={`rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden ${MODAL_TEXT}`}
      >
        <div className="bg-rose-500 px-6 py-4">
          <h2 className="text-xl md:text-2xl font-semibold text-white">
            Staff
          </h2>
        </div>
        <div className="px-6 py-6 space-y-6">{wizardBody}</div>
      </div>
      {rollbackModal}
    </>
  );
}
